<template>
  <div class="home">
    <b-container class="container-rda">
      <b-row>
        <b-col>
          <Breadcrumb :breadcrumb="breadcrumb" />
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <h2 class="purple bold title">
            Fila de espera <span class="tags_count">{{ count }}</span>
          </h2>
        </b-col>
      </b-row>

      <b-row class="mt-50">
        <b-col md="3">
          <SelectSimple
            label="Status"
            v-model="selectedStatus"
            :options="statusOptions"
            :callback="selectStatus"
          />
          <span
            class="clear-field"
            v-if="filters.status != null"
            @click="clearStatus"
            >Limpar filtro</span
          >
        </b-col>
        <b-col md="3">
          <SelectCalendar
            label="Ano Letivo"
            v-model="filters.calendar_id"
            :callback="selectCalendar"
          ></SelectCalendar>
          <span
            class="clear-field"
            v-if="filters.calendar_id != null"
            @click="clearCalendar"
            >Limpar filtro</span
          >
        </b-col>
        <b-col md="6">
          <SelectSimple
            v-if="cycleOptions.length"
            label="Ciclo"
            v-model="selectedCycle"
            :options="cycleOptions"
            :callback="selectCycle"
          />
          <span
            class="clear-field"
            v-if="selectedCycle != null"
            @click="clearCycle"
            >Limpar filtro</span
          >
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <DataTable
            :items="rows"
            :fields="fields"
            :busy="loading"
            :show-empty="isRequestSend"
          >
            <template #cell(queue_position)="{ item }">
              <span v-if="item.queue_position" class="queue-position"
                >{{ item.queue_position }}º</span
              >
              <span v-else class="no-position">-</span>
            </template>

            <template #cell(enrollment_id)="{ item }">
              <p>#{{ item.enrollment_id }}</p>
            </template>

            <template #cell(student_name)="{ item }">
              <p>{{ item.student_name }}</p>
            </template>

            <template #cell(responsible_name)="{ item }">
              <div class="responsible">
                <p class="responsible-name">{{ item.responsible_name }}</p>
                <span v-if="item.responsible_email" class="responsible-email">{{
                  item.responsible_email
                }}</span>
                <div
                  v-if="item.responsible_phones.length"
                  class="responsible-phone"
                >
                  <span class="phone-number">{{
                    item.responsible_phones[0].number
                  }}</span>
                  <span
                    v-if="item.responsible_phones[0].is_mobile"
                    class="phone-type"
                    >celular</span
                  >
                  <PopoverIcon
                    v-if="item.responsible_phones.length > 1"
                    :id="`responsible-phones-${item.id}`"
                    class="phones-popover"
                    title="Telefones"
                  >
                    <template #icon>
                      <span class="more-phones"
                        >+{{ item.responsible_phones.length - 1 }}</span
                      >
                    </template>

                    <ul class="phones-list">
                      <li
                        v-for="phone in item.responsible_phones"
                        :key="phone.number"
                      >
                        <span class="phone-number">{{ phone.number }}</span>
                        <span v-if="phone.is_mobile" class="phone-type"
                          >celular</span
                        >
                      </li>
                    </ul>
                  </PopoverIcon>
                </div>
              </div>
            </template>

            <template #cell(enrollment_description)="{ item }">
              <span :class="`segment-tag ${cycleSegment(item).key}`">{{
                cycleSegment(item).label
              }}</span>
              <p class="cycle-description">
                {{ item.enrollment_description }}
                <span v-if="item.time_period" class="time-period"
                  >· {{ item.time_period }}</span
                >
              </p>
            </template>

            <template #cell(status)="{ item }">
              <span :class="`status-tag ${item.status}`">{{
                waitingListStatusLabel(item.status)
              }}</span>
            </template>

            <template #cell(entered_at)="{ item }">
              <p class="entered-at">{{ formatDate(item.entered_at) }}</p>
            </template>

            <template #cell(actions)="{ item }">
              <div v-if="hasWaitingListActions(item.status)" class="actions">
                <button
                  v-if="
                    canChangeWaitingListStatus(item.status, statuses.ACTIVE)
                  "
                  type="button"
                  class="action-btn release"
                  @click="updateStatus(item, statuses.ACTIVE)"
                >
                  Liberar matrícula
                </button>
                <button
                  v-if="
                    canChangeWaitingListStatus(item.status, statuses.INACTIVE)
                  "
                  type="button"
                  class="action-btn remove"
                  @click="updateStatus(item, statuses.INACTIVE)"
                >
                  Remover da lista de espera
                </button>
              </div>
              <span v-else class="no-actions">Nenhuma ação disponível</span>
            </template>
          </DataTable>

          <b-row class="align-center" v-if="count > perPage">
            <b-pagination
              v-model="page"
              :per-page="perPage"
              :total-rows="count"
              @change="getList"
              last-text
              first-text
            >
              <template #prev-text>
                <LeftArrowIcon class="left" />
              </template>
              <template #next-text>
                <LeftArrowIcon />
              </template>
            </b-pagination>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import moment from "moment";
import DataTable from "@/components/table/DataTable.vue";
import PopoverIcon from "@/components/table/PopoverIcon.vue";
import LeftArrowIcon from "@/components/svg/LeftArrowIcon.vue";
import SelectCalendar from "@/components/inputs/SelectCalendar.vue";
import SelectSimple from "@/components/inputs/SelectSimple.vue";
import WaitingListService from "@/services/waitingList.service";
import {
  WAITING_LIST_POPUP_OPTIONS,
  WAITING_LIST_STATUSES,
  WAITING_LIST_STATUS_LABELS,
  canChangeWaitingListStatus,
  cycleSegment,
  hasWaitingListActions,
  waitingListStatusLabel,
} from "@/utils/waitingList";

const ALL_STATUSES_OPTION = { id: null, label: "Todos" };
const DEFAULT_STATUS_OPTION = {
  id: WAITING_LIST_STATUSES.WAITING,
  label: WAITING_LIST_STATUS_LABELS[WAITING_LIST_STATUSES.WAITING],
};

export default {
  name: "fila-de-espera",
  components: {
    DataTable,
    LeftArrowIcon,
    PopoverIcon,
    SelectCalendar,
    SelectSimple,
  },
  data() {
    return {
      rows: [],
      count: 0,
      page: 1,
      perPage: 20,
      loading: false,
      isRequestSend: false,
      statuses: WAITING_LIST_STATUSES,
      filters: {
        status: DEFAULT_STATUS_OPTION.id,
        calendar_id: null,
        cycle_id: null,
      },
      selectedStatus: DEFAULT_STATUS_OPTION,
      selectedCycle: null,
      cycleOptions: [],
      fields: [
        { key: "queue_position", label: "Posição" },
        { key: "entered_at", label: "Entrada" },
        {
          key: "calendar_designation",
          label: "Ano letivo",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "enrollment_description", label: "Ciclo e turno" },
        { key: "enrollment_id", label: "Matrícula" },
        { key: "student_name", label: "Aluno" },
        { key: "responsible_name", label: "Responsável" },
        { key: "status", label: "Status" },
        { key: "actions", label: "Ações" },
      ],
      breadcrumb: [
        {
          text: "Secretaria",
          href: "/secretaria",
        },
        {
          text: "Fila de espera",
          active: true,
        },
      ],
    };
  },

  computed: {
    statusOptions() {
      return [
        ALL_STATUSES_OPTION,
        ...Object.entries(WAITING_LIST_STATUS_LABELS).map(([id, label]) => ({
          id,
          label,
        })),
      ];
    },
  },

  methods: {
    canChangeWaitingListStatus,
    hasWaitingListActions,
    cycleSegment,
    waitingListStatusLabel,

    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY HH:mm") : "-";
    },

    async getCycleOptions() {
      const response = await WaitingListService.getEnrollmentCycles();

      this.cycleOptions = Object.values(response.data.cycles)
        .flat()
        .filter(
          (cycleGrade, index, cycleGrades) =>
            cycleGrades.findIndex(
              (other) => other.cycle_id === cycleGrade.cycle_id
            ) === index
        )
        .map((cycleGrade) => ({
          id: cycleGrade.cycle_id,
          label: cycleGrade.cycle_name,
        }));
    },

    applyQueryFilters() {
      const { status, calendar_id } = this.$route.query;
      const statusOption = this.statusOptions.find(
        (option) => option.id && option.id === status
      );

      if (statusOption) {
        this.selectedStatus = statusOption;
        this.filters.status = statusOption.id;
      }

      if (calendar_id && !Number.isNaN(Number(calendar_id))) {
        this.filters.calendar_id = Number(calendar_id);
      }
    },

    selectStatus(option) {
      this.selectedStatus = option;
      this.filters.status = option.id;
      this.getList(1);
    },

    clearStatus() {
      this.selectedStatus = ALL_STATUSES_OPTION;
      this.filters.status = null;
      this.getList(1);
    },

    selectCalendar({ id }) {
      this.filters.calendar_id = id;
      this.getList(1);
    },

    clearCalendar() {
      this.filters.calendar_id = null;
      this.getList(1);
    },

    selectCycle(option) {
      this.selectedCycle = option;
      this.filters.cycle_id = option.id;
      this.getList(1);
    },

    clearCycle() {
      this.selectedCycle = null;
      this.filters.cycle_id = null;
      this.getList(1);
    },

    async getList(page = 1) {
      this.page = page;
      this.loading = true;

      try {
        const params = { page: this.page, limit: this.perPage };

        Object.entries(this.filters).forEach(([key, value]) => {
          if (value) {
            params[key] = value;
          }
        });

        const response = await WaitingListService.getAll(params);

        this.rows = response.data.rows;
        this.count = response.data.count;
        this.isRequestSend = true;
      } catch (error) {
        this.$swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Não foi possível carregar a lista de espera.",
        });
      } finally {
        this.loading = false;
      }
    },

    async updateStatus(row, status) {
      const result = await this.$swal.fire({
        ...WAITING_LIST_POPUP_OPTIONS,
        title:
          status === WAITING_LIST_STATUSES.ACTIVE
            ? "Liberar matrícula?"
            : "Remover da lista de espera?",
        text:
          status === WAITING_LIST_STATUSES.ACTIVE
            ? `${row.student_name} poderá continuar a matrícula.`
            : `${row.student_name} continuará sem poder avançar na matrícula.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#68c5b7",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        await WaitingListService.updateStatus(row.enrollment_id, status);

        this.$swal.fire({
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 5000,
          icon: "success",
          title: "Status atualizado com sucesso!",
        });

        this.getList(this.page);
      } catch (error) {
        this.$swal.fire({
          icon: "error",
          title: "Oops...",
          text: error.response?.data?.msg || "Não foi possível atualizar!",
        });
      }
    },
  },

  created() {
    this.applyQueryFilters();
    this.getCycleOptions();
    this.getList();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.responsible {
  p {
    margin: 0;
  }
}

.responsible-name {
  font-size: 15px;
  font-weight: $bold;
}

.responsible-email,
.phone-number {
  font-size: 14px;
}

.responsible-email {
  display: block;
  word-break: break-all;
}

.responsible-phone {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  white-space: nowrap;
}

.phone-type {
  color: $grey1;
  font-size: 12px;
}

.phones-popover {
  align-items: center;
  display: inline-flex;
}

.more-phones {
  background-color: $grey5;
  border-radius: 10px;
  cursor: pointer;
  display: inline-block;
  font-size: 12px;
  font-weight: $bold;
  line-height: 18px;
  padding: 1px 6px 0;
}

.phones-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li + li {
    margin-top: 4px;
  }
}

.title {
  align-items: center;
  display: flex;
  justify-content: flex-start;

  .tags_count {
    background-color: #68c5b7;
    border-radius: 3px;
    color: #fff;
    font-size: 18px;
    height: max-content;
    margin-left: 10px;
    padding: 3px 10px;
  }
}

.queue-position {
  align-items: center;
  background-color: $purple;
  border-radius: 50%;
  color: $white;
  display: inline-flex;
  font-size: 14px;
  font-weight: $bold;
  height: 34px;
  justify-content: center;
  padding-top: 2px;
  width: 34px;
}

.no-position {
  color: $grey5;
}

.segment-tag {
  background-color: $grey7;
  border-radius: 5px;
  color: $black2;
  display: inline-block;
  font-size: 12px;
  font-weight: $bold;
  line-height: 18px;
  margin-bottom: 4px;
  padding: 1px 8px 0;

  &.pititim {
    background-color: $pink;
  }

  &.infantil {
    background-color: $yellow2;
  }

  &.fundamental {
    background-color: $blue2;
  }

  &.ensinoMedio {
    background-color: $violet;
  }
}

.cycle-description,
.entered-at {
  font-size: 14px;
}

.entered-at {
  white-space: nowrap;
}

.time-period {
  color: $grey1;
  font-weight: $bold;
  text-transform: capitalize;
}

.actions {
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
}

.no-actions {
  color: $grey4;
  font-size: 13px;
  font-style: italic;
}

.action-btn {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: $bold;
  justify-content: center;
  line-height: 1.3;
  padding: 6px 12px 4px;
  transition: background-color 0.15s, color 0.15s;
  white-space: nowrap;

  &.release {
    background-color: $blue;
    color: $white;

    &:hover {
      background-color: darken($blue, 8%);
    }
  }

  &.remove {
    background-color: transparent;
    border-color: $red;
    color: $red;

    &:hover {
      background-color: rgba($red, 0.1);
    }
  }
}

.status-tag {
  align-items: center;
  border-radius: 12px;
  display: inline-flex;
  font-size: 13px;
  font-weight: $bold;
  gap: 6px;
  line-height: 1.3;
  padding: 4px 10px;
  white-space: nowrap;
  background-color: rgba($grey4, 0.12);
  color: $grey4;

  &.waiting {
    background-color: rgba($orange2, 0.15);
    color: darken($orange2, 27%);

    &::before {
      animation: status-pulse 1.6s ease-out infinite;
      background-color: currentColor;
      border-radius: 50%;
      content: "";
      flex-shrink: 0;
      height: 6px;
      width: 6px;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
  }

  &.active {
    background-color: rgba($green1, 0.15);
    color: darken($green1, 15%);
  }

  &.inactive {
    background-color: rgba($red, 0.12);
    color: darken($red, 15%);
  }
}

@keyframes status-pulse {
  0% {
    box-shadow: 0 0 0 0 currentColor;
  }

  100% {
    box-shadow: 0 0 0 6px transparent;
  }
}
</style>
